import type { FetchError } from 'ofetch';
import type { _GettersTree } from 'pinia';

import { defineStore } from 'pinia';

import type { Wishlist, WishlistItem, WishlistRequest } from '~/stores/wishlist/types';

import { ApiMethod, handleApiError } from '~/utils/apiClient';
import { FetchState } from '~/utils/common';
import { StorageKey } from '~/utils/storage';

import { useAuthStore } from '../auth/authStore';

const STORE_ID = 'wishlist';

interface Actions {
  fetchWishlist: (locale: Locale) => Promise<Wishlist | null>;
  updateWishlist: (data: WishlistRequest) => Promise<Wishlist | null>;
}

interface Getters extends _GettersTree<State> {
  getWishlistItemsTotal: (state: State) => number;
  getWishlistLineItems: (state: State) => WishlistItem[] | null;
}

interface State {
  wishlist: Wishlist | null;
  wishlistFetchState: FetchState;
}

export const useWishlistStore = defineStore<typeof STORE_ID, State, Getters, Actions>(STORE_ID, {
  actions: {
    async fetchWishlist(locale: Locale) {
      this.wishlistFetchState = FetchState.IN_PROGRESS;

      try {
        const response = await this.$api.fetch<Wishlist>(ApiMethod.GET, '/wishlists/me', { locale }, true);

        if (response?.data) {
          this.wishlist = response.data;
          this.wishlistFetchState = FetchState.SUCCEEDED;
          return response.data;
        }

        this.wishlistFetchState = FetchState.FINISHED;
        return null;
      } catch (error) {
        const fetchError = error as FetchError;
        await handleApiError(fetchError);

        this.wishlistFetchState = FetchState.ERRORED;
        return null;
      }
    },
    async updateWishlist(data: WishlistRequest) {
      const authStore = useAuthStore();

      try {
        const response = await this.$api.fetch<Wishlist>(ApiMethod.PUT, '/wishlists', { ...data }, true);

        if (response?.data) {
          this.wishlist = response.data;

          if (!authStore.isLoggedIn) saveItemStorage(StorageKey.CURRENT_CART, response.data.id);

          this.wishlistFetchState = FetchState.SUCCEEDED;

          return response.data;
        }
      } catch (error) {
        const fetchError = error as FetchError;
        await handleApiError(fetchError);

        this.wishlistFetchState = FetchState.ERRORED;
        return null;
      }

      this.wishlistFetchState = FetchState.FINISHED;

      return null;
    },
  },
  getters: {
    getWishlistItemsTotal: (state: State) => state.wishlist?.items.length ?? 0,
    getWishlistLineItems: (state: State) =>
      state.wishlist?.items.sort((a, b) =>
        a.added_at && b.added_at && new Date(a.added_at) > new Date(b.added_at) ? -1 : 1,
      ) ?? [],
  },
  state: () => ({ wishlist: null, wishlistFetchState: FetchState.INITIAL }),
});
